// Walking-bout detection: isolate the steady-state walking segment.
//
// A capture usually contains a few non-gait frames at the start (the patient
// is static / accelerating) and end (decelerating / turning). Analysing those
// corrupts cadence, stance %, step length, etc. We detect the longest
// constant-direction, above-threshold-speed segment of the pelvis trajectory
// and analyse only that. Pure + testable.

import { Joints, type PoseFrame } from "../pose/landmarks";
import { oneEuroSeries } from "../pose/smoothing";
import { percentile } from "./geometry";

export interface Bout {
  startIdx: number;
  endIdx: number;
  startMs: number;
  endMs: number;
}

export function detectWalkingBout(frames: PoseFrame[]): Bout {
  const n = frames.length;
  const full: Bout = { startIdx: 0, endIdx: n - 1, startMs: frames[0]?.tMs ?? 0, endMs: frames[n - 1]?.tMs ?? 0 };
  if (n < 8) return full;

  const ts = frames.map((f) => f.tMs);
  const px = oneEuroSeries(
    frames.map((f) => new Joints(f).pelvisCenter().x),
    ts,
    { minCutoff: 1.2, beta: 0.005 },
  );

  // Central-difference velocity (normalized units per second).
  const v: number[] = new Array(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    const dt = (ts[i + 1] - ts[i - 1]) / 1000 || 1e-3;
    v[i] = (px[i + 1] - px[i - 1]) / dt;
  }
  v[0] = v[1];
  v[n - 1] = v[n - 2];

  const speeds = v.map(Math.abs).filter((s) => s > 1e-4);
  if (speeds.length === 0) return full;
  const medSpeed = percentile(speeds, 50);
  const thr = Math.max(0.15 * medSpeed, 1e-3);

  // Find the longest run with a consistent sign and speed above threshold,
  // tolerating short dropouts.
  let best = { start: 0, end: 0, len: 0 };
  let runStart = -1;
  let runSign = 0;
  let gap = 0;
  const maxGap = 3;

  const closeRun = (end: number) => {
    if (runStart >= 0) {
      const len = end - runStart + 1;
      if (len > best.len) best = { start: runStart, end, len };
    }
    runStart = -1;
    runSign = 0;
    gap = 0;
  };

  for (let i = 0; i < n; i++) {
    const moving = Math.abs(v[i]) >= thr;
    const sign = Math.sign(v[i]);
    if (moving) {
      if (runStart < 0) {
        runStart = i;
        runSign = sign;
        gap = 0;
      } else if (sign === runSign || sign === 0) {
        gap = 0;
      } else {
        // direction reversed → close current run, start a new one
        closeRun(i - 1 - gap);
        runStart = i;
        runSign = sign;
      }
    } else if (runStart >= 0) {
      gap++;
      if (gap > maxGap) closeRun(i - gap);
    }
  }
  closeRun(n - 1 - gap);

  // Require a meaningful bout (~1s); otherwise fall back to the full clip.
  const durMs = ts[best.end] - ts[best.start];
  if (best.len < 6 || durMs < 1000) return full;

  // Trim a small margin to avoid accel/decel edges (~120 ms each side).
  const dtMed = (ts[n - 1] - ts[0]) / (n - 1) || 16.7;
  const margin = Math.min(Math.floor(best.len / 6), Math.round(120 / dtMed));
  const startIdx = Math.min(best.start + margin, best.end);
  const endIdx = Math.max(best.end - margin, startIdx);

  return { startIdx, endIdx, startMs: ts[startIdx], endMs: ts[endIdx] };
}
